import { useNavigate } from "react-router-dom"
import { Data2, DataTypeForm2, useDescribeHooks, DataTypeSelectorTable } from "../../../../../features/data-types"
import { useRoutesHooks } from "../../../../../features/routes"
import { Column, FieldType, FieldValue, FormField, O, option, Option, Validatable } from "../../../../../features/ui"
import { Roadmap } from "../../../state/types"
import { DriverField } from "../../driver/driver-field/driverField"
import { VehicleField } from "../../vehicle/vehicle-field/vehicleField"
import { TableName } from "../../../../../tables"
import { useEffect, useMemo, useState } from "react"
import { useActions } from "./actions"
import { Address, AddressData } from "../../../../../features/address"
import { useBranchLoaders, useManagerHooks } from "../../../../manager"
import { RouteDetail } from "../route-detail/routeDetail"
import { ProgressBar } from "./progressBar"
import { Measure } from "../../../../common"
import { useVehicleLoaders } from "../../../loaders/vehicleLoaders"
import { useUnitTypeLoaders } from "../../../../stock"
import { RoadmapWeightWarningModal } from "./roadmapWeightWarningModal"
import { Delivery, DeliveryOrder, useDeliveryHooks } from "../../../../deliveries"
import { Operation } from "../../../../operations"

type Props = {
    shipments: Delivery[]
    shipmentOrders: DeliveryOrder[]
    operations: Operation[]
}

export const RoadmapForm = (props: Props) => {
    const { shipments, shipmentOrders, operations } = props
    const { dataType } = useRoutesHooks().parseUrl()
    const roadmap = dataType as O<Roadmap>
    const createMode = !roadmap

    const [deliveryDescriptions, setDeliveryDescriptions] = useState<Map<string, string>>(new Map())
    const [shipmentsData, setShipmentsData] = useState<Data2[]>([])
    const [shipmentsValidations, setShipmentsValidations] = useState<string[]>([])
    const [routeAddresses, setRouteAddresses] = useState<Map<string, Address>>(new Map())
    const [sortedShipmentsData, setSortedShipmentsData] = useState<Data2[]>([])
    const [totalShipmentsWeight, setTotalShipmentsWeight] = useState<O<number>>()
    const [vehicleMaxWeight, setVehicleMaxWeight] = useState<O<Measure>>()
    const [loadingShipments, setLoadingShipments] = useState(true)
    const [modalData, setModalData] = useState<O<Data2>>()

    const { currentBranchId } = useManagerHooks()
    const { describeDelivery, getDeliveryAddress, describeWeight, getWeight } = useDeliveryHooks()
    const { describe } = useDescribeHooks()
    const navigate = useNavigate()

    const branchLoaders = useBranchLoaders()
    const vehicleLoaders = useVehicleLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()

    const { submitRoadmapOrOpenModal, submitRoadmap } = useActions()

    const findShipment = (id?: string) => shipments.find(shipment => shipment.id === id)
    const findOrder = (id?: string) => shipmentOrders.find(order => order.id === id)
    const findOperation = (id?: string) => operations.find(operation => operation.id === id)
    const findDeliveryDescription = (id: string) => deliveryDescriptions.get(id)
    
    const loadDeliveryDescriptions = async () => {
        const descriptions = await Promise.all(shipments.map(shipment => describeDelivery(shipment, findOrder(shipment.orderId)!)))
        return new Map(descriptions.map((description, index) => [shipments[index].id, description]))
    }

    useEffect(() => {
        setLoadingShipments(true)
        loadDeliveryDescriptions()
            .then(setDeliveryDescriptions)
            .finally(() => setLoadingShipments(false))
    }, [shipments, shipmentOrders])

    const loadShipmentsRelatedData = async () => {
        const { findBranch } = branchLoaders()
        const branch = findBranch(currentBranchId)!
        const branchAddress = branch.address
        const selectedShipments = shipmentsData.map(shipmentData => findShipment(shipmentData.id! as string)!)
        
        const newRouteAddresses = new Map<string, Address>().set('start', branchAddress)
        if (selectedShipments.length > 0) {
            for (const shipment of selectedShipments) {
                newRouteAddresses.set(shipment.id, await getDeliveryAddress(shipment))
            }
        }
        newRouteAddresses.set('end', branchAddress)
        setRouteAddresses(newRouteAddresses)

        const weights = await Promise.all(selectedShipments.map(shipment => getWeight(shipment)))
        setTotalShipmentsWeight(weights.reduce((acc, weight) => acc + weight.units, 0))
    }

    useEffect(() => {
        loadShipmentsRelatedData()
    }, [shipmentsData])

    const shipmentColumns: Column[] = [
        {
            name: 'code',
            type: FieldType.TEXT,
            label: 'Código'
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente'
        },
        {
            name: 'locality',
            type: FieldType.TEXT,
            label: 'Localidad'
        },
        {
            name: 'weight',
            type: FieldType.TEXT,
            label: 'Peso'
        }
    ]

    const shipmentOptions = useMemo(() => deliveryDescriptions.size > 0 ? shipments
        .sort((shipment_A, shipment_B) => shipment_A.date.localeCompare(shipment_B.date))
        .map(shipment => option(shipment.id, findDeliveryDescription(shipment.id)!)) : []
    , [shipments, deliveryDescriptions])

    const shipmentRowData = async (id: string) => {
        const shipment = findShipment(id)!
        const operation = findOperation(shipment.operationId)!
        const address = await getDeliveryAddress(shipment)
        const weight = await describeWeight(shipment)
        
        return {
            id: shipment.id,
            code: describe(shipment.table, operation),
            date: shipment.date,
            customerId: shipment.customerId,
            locality: address.locality,
            weight
        }
    }

    const onChangeVehicle = (value?: FieldValue) => {
        const vehicle = vehicleLoaders().findVehicle(value?.toString())
        setVehicleMaxWeight(vehicle?.maxLoadWeight)
    }

    const renderShipmentOption = async (
        option: Option,
        onClick?: (e: React.MouseEvent<HTMLElement>) => void
    ) => {
        const description = findDeliveryDescription(option.value)
        return <div onClick={onClick}>{description}</div>
    }

    const onChangeShipments = async (data: Data2[]) => {
        setShipmentsData(data)
        setShipmentsValidations([])
    }

    const onChangeSortedAddresses = (sortedAddressesData: AddressData[] = []) => {
        const newSortedShipmentsData = sortedAddressesData.slice(1, -1).map((sortedAddressData, index) => {
            const shipmentData = shipmentsData.find(shipmentData => shipmentData.id === sortedAddressData.key)
            return {
                ...shipmentData,
                order: index + 1
            }
        })
        setSortedShipmentsData(newSortedShipmentsData)
    }

    const fields: FormField[] = [
        DriverField({}),
        VehicleField({
            onChange: onChangeVehicle
        }),
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha',
            defaultValue: new Date()
        },
        {
            name: 'loadBar',
            type: FieldType.PERCENTAGE,
            label: 'Carga Máxima',
            narrow: true,
            render: () => vehicleMaxWeight ? (
                <ProgressBar
                    current={totalShipmentsWeight || 0}
                    total={vehicleMaxWeight?.units || 0}
                    totalLabel={unitTypeLoaders().findUnitType(vehicleMaxWeight?.unitTypeId)?.abbreviation}
                />
            ) : <div>Seleccione un vehículo...</div>
        },
        {
            name: 'shipments',
            type: FieldType.TABLE,
            loading: loadingShipments,
            render: () => (
                <Validatable validations={shipmentsValidations}>
                    <DataTypeSelectorTable
                        columns={shipmentColumns}
                        options={shipmentOptions}
                        placeholder="Seleccionar envíos"
                        emptyMessage="Todos los remitos ya fueron asignados a una hoja de ruta."
                        rowData={shipmentRowData}
                        renderOption={renderShipmentOption}
                        onChange={onChangeShipments}
                    />
                </Validatable>
            )
        }
    ]

    if (routeAddresses.size > 2) {
        const mapField = {
            name: 'map',
            type: FieldType.IMAGE,
            render: () => <RouteDetail
                addresses={routeAddresses}
                onChangeSortedAddresses={onChangeSortedAddresses}
            />
        }
        fields.push(mapField)
    }

    const validate = (): boolean => {
        if (shipmentsData.length === 0 || sortedShipmentsData.length === 0) {
            setShipmentsValidations(['Debe agregar al menos 1 envío'])
            return false
        }
        
        return true
    }

    const onSubmit = (data: Data2) => {
        if (validate()) {
            setModalData(data)
            const isOverloaded = totalShipmentsWeight! > vehicleMaxWeight!.units
            submitRoadmapOrOpenModal(data, currentBranchId!, sortedShipmentsData, isOverloaded)
        }
    }

    const onCancel = () => navigate(-1)

    const onSaveRoadmap = () => {
        submitRoadmap(modalData!, currentBranchId!, sortedShipmentsData)
    }

    return (
        <>
            <DataTypeForm2
                formId="roadmap-form"
                fields={fields}
                createMode={createMode}
                defaultDataType={roadmap}
                disableSubmit={shipmentsData.length === 0}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            <RoadmapWeightWarningModal
                onSubmit={onSaveRoadmap}
            />
        </>
    )
}
